// src/hooks/useNotifications.ts
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
  doc,
  updateDoc,
  deleteDoc,
} from 'firebase/firestore';
import { auth, db } from '../services/firebase';

export interface AppNotification {
  id: string;
  feed_item_id: string;
  created_at: Date | null;
  seen: boolean;
  dismissed: boolean;
  title: string;
  rationale?: string;
  /** Feed item ids covered by a digested notification. 1+ entries.
   *  When length > 1, the notification consolidates multiple picks
   *  the user hasn't seen yet. */
  merged_item_ids?: string[];
  /** Display titles of the merged items. */
  merged_item_titles?: string[];
}

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      setIsLoading(false);
      return;
    }

    const ref = collection(db, 'users', user.uid, 'notifications');
    // Keep this light: 30 most-recent notifications.
    const q = query(ref, orderBy('created_at', 'desc'), limit(30));

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const out: AppNotification[] = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          if (data.dismissed) return;
          out.push({
            id: docSnap.id,
            feed_item_id: data.feed_item_id || '',
            created_at:
              data.created_at instanceof Timestamp
                ? data.created_at.toDate()
                : null,
            seen: !!data.seen,
            dismissed: !!data.dismissed,
            title: data.title || '',
            rationale: data.rationale,
            merged_item_ids: Array.isArray(data.merged_item_ids)
              ? (data.merged_item_ids as string[])
              : undefined,
            merged_item_titles: Array.isArray(data.merged_item_titles)
              ? (data.merged_item_titles as string[])
              : undefined,
          });
        });
        setNotifications(out);
        setIsLoading(false);
      },
      (err) => {
        console.warn('Notifications listener error:', err);
        setIsLoading(false);
      }
    );

    return () => unsub();
  }, []);

  const unseenCount = useMemo(
    () => notifications.filter((n) => !n.seen).length,
    [notifications]
  );

  const markSeen = useCallback(async (notifId: string) => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      await updateDoc(doc(db, 'users', user.uid, 'notifications', notifId), {
        seen: true,
      });
    } catch (err) {
      console.warn('markSeen failed:', err);
    }
  }, []);

  const markAllSeen = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return;
    const unseen = notifications.filter((n) => !n.seen);
    await Promise.all(
      unseen.map((n) =>
        updateDoc(doc(db, 'users', user.uid, 'notifications', n.id), {
          seen: true,
        }).catch((err) => console.warn('markAllSeen partial fail:', err))
      )
    );
  }, [notifications]);

  const dismiss = useCallback(async (notifId: string) => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      await deleteDoc(doc(db, 'users', user.uid, 'notifications', notifId));
    } catch (err) {
      console.warn('dismiss failed:', err);
    }
  }, []);

  return {
    notifications,
    unseenCount,
    isLoading,
    markSeen,
    markAllSeen,
    dismiss,
  };
};
